import { TRACK_SWITCH_ACKNOWLEDGEMENT } from '@/components/SwitchTrackModal/trackTransition';
import { CurrentUserContext } from '@/contexts/contexts';
import { answerString, PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { PN_EMPLOYER_LOCATION_ID } from '@bariendo/lib/pn-consultation-v2-script';
import { PacWidget } from '@bariendo/lib/patient-access-script';
import {
  describeEmployerBenefit,
  describeVerificationTask,
  readVerificationTask,
  verificationTaskValue,
} from '@bariendo/lib/pn-consultation-script';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useContext } from 'react';
import { EMPLOYER_VERIFICATION_TASK_TITLE } from '@bariendo/lib/coverage-verification-task';
import { employerTaskDescription } from './employerTaskDetails';
import { TRACK_BY_ADMINISTRATOR } from '@bariendo/lib/episode-of-care';
import { moveToFinancingApproval } from './financingApproval';
import { useCoverageVerification } from './useCoverageVerification';
import { VerificationTaskButton, VerificationTaskDone } from './VerificationTaskButton';

interface EmployerVerificationTaskProps {
  widget: Extract<PacWidget, { kind: 'verificationTask' }>;
  /** The answers that apply. */
  answers: PatientAccessCallAnswers;
  /** Records what the button did, and saves it at once so a retry can't repeat it. */
  onDone: (id: string, value: string) => void;
  patient?: Patient;
  readOnly?: boolean;
  /** v2.0 (BAR-3002): the button also sets the Location and moves the patient to Financing and approval. */
  financing?: boolean;
}

/**
 * The employer section's next step (BAR-2921): "Move to Employer track and
 * create verification task", or "Create verification task" when the patient
 * is already on the matched partner track. Only shown after a partner match,
 * so the target is always Carrum or Transcarent.
 */
export function EmployerVerificationTask({
  widget,
  answers,
  onDone,
  patient,
  readOnly,
  financing = false,
}: EmployerVerificationTaskProps) {
  const medplum = useMedplum();
  // `useContext`, not `useCurrentUser`: no provider means no role.
  const canSwitch = useContext(CurrentUserContext)?.currentUserPermissions.canAccessPayments ?? false;
  const benefit = describeEmployerBenefit(answers);
  const target = benefit?.covered ? TRACK_BY_ADMINISTRATOR[benefit.administrator] : undefined;
  const { trackLoading, switching, needsAcknowledgement, run } = useCoverageVerification(
    readOnly ? undefined : patient,
    target
  );

  // Done for this match only: a new match (another administrator) is a new
  // step. A saved note shows whatever was done.
  const record = readVerificationTask(answers.values[widget.id]);
  const done = describeVerificationTask(answers.values[widget.id], 'Employer');
  if (readOnly) return done ? <VerificationTaskDone text={done} /> : null;
  if (done && record?.track === target) return <VerificationTaskDone text={done} />;

  return (
    <VerificationTaskButton
      label={
        financing
          ? switching
            ? 'Move to Employer track and Financing and approval'
            : 'Move to Financing and approval'
          : switching
            ? 'Move to Employer track and create verification task'
            : 'Create verification task'
      }
      unavailableText={canSwitch ? undefined : 'Payments role needed to move the track or create the task'}
      acknowledgementLabel={needsAcknowledgement ? TRACK_SWITCH_ACKNOWLEDGEMENT : undefined}
      loading={trackLoading}
      onRun={async (acknowledged) => {
        const outcome = await run(
          { title: EMPLOYER_VERIFICATION_TASK_TITLE, description: employerTaskDescription(answers) },
          acknowledged
        );
        if (!target) return;
        // The record waits for the stage, so a failure there leaves the button
        // to retry; the open task is found again, not created twice.
        const stage =
          financing && patient?.id
            ? await moveToFinancingApproval(medplum, patient.id, answerString(answers.values[PN_EMPLOYER_LOCATION_ID]))
            : undefined;
        onDone(widget.id, verificationTaskValue(outcome, target, stage));
      }}
    />
  );
}
